/**
 * Small presentational primitives shared by the four insights panels.
 * Module-local on purpose — src/components/ is owned elsewhere.
 *
 * Everything here obeys the house rules: warm tokens from src/styles.css,
 * >=44px touch targets, visible focus, never status-by-color-alone.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { ApiRequestError } from '../../lib/api';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Resolve a design token from src/styles.css to the value it actually holds
 * ('--color-sage' -> '#2f6f4e').
 *
 * Anything we persist (a savings goal's color) has to be a real color, not a
 * `var()` reference — but the hex must never be typed into module code, or the
 * palette lives in three places and drifts. Read it from the stylesheet instead:
 * one source of truth. Returns '' if the token is unknown, and callers then let
 * the D1 column default decide rather than inventing a color.
 */
export function tokenColor(token: string): string {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(token).trim();
}

export function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.isForbidden) return 'This section is for adults only.';
    return err.message || 'The request failed.';
  }
  return 'Could not reach the household server. Check your connection and try again.';
}

// ---------------------------------------------------------------- data loading

interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: (value: T) => void;
}

/** Load-once-with-reload. Ignores results from a stale request. */
export function useResource<T>(load: () => Promise<T>, deps: unknown[]): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(errorText(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload, setData };
}

// ---------------------------------------------------------------- surfaces

export function Card({
  children,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'li';
}) {
  return (
    <Tag className={cx('rounded-card border border-border bg-surface p-4 sm:p-5', className)}>
      {children}
    </Tag>
  );
}

export function PanelHeading({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">{title}</h2>
        {sub && <p className="mt-1 max-w-prose text-sm text-ink-soft">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------- controls

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
};

const BUTTON_STYLES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90 border-transparent',
  secondary: 'bg-surface text-ink border-border hover:bg-surface-sunk',
  ghost: 'bg-transparent text-ink-soft border-transparent hover:bg-surface-sunk hover:text-ink',
  danger: 'bg-transparent text-clay border-border hover:bg-clay-soft',
};

export function Button({ variant = 'secondary', className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border px-4 text-sm font-medium',
        'transition-colors focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
        'focus-visible:ring-offset-canvas focus-visible:outline-none disabled:opacity-50',
        BUTTON_STYLES[variant],
        className
      )}
    />
  );
}

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cx('block', className)}>
      <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-soft">{hint}</span>}
    </label>
  );
}

export const inputClass =
  'block min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-base text-ink ' +
  'placeholder:text-ink-faint focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage/40 ' +
  'focus-visible:outline-none';

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(inputClass, props.className)} />;
}

// ---------------------------------------------------------------- states

export function Loading({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-live="polite" className="space-y-3">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-card border border-border bg-surface-sunk"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card className="border-clay/40 bg-clay-soft">
      <p className="text-sm font-medium text-ink">Something went wrong</p>
      <p className="mt-1 text-sm text-ink-soft">{message}</p>
      <Button variant="secondary" className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card className="bg-surface-sunk text-center">
      <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
      <div className="mx-auto mt-2 max-w-prose text-sm leading-relaxed text-ink-soft">{children}</div>
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </Card>
  );
}

/** PRD 8: financial output is illustrative, never advice. */
export function Disclaimer({ children }: { children?: ReactNode }) {
  return (
    <p className="mt-4 rounded-lg border border-border bg-surface-sunk px-3 py-2 text-xs leading-relaxed text-ink-soft">
      <span className="font-medium text-ink">Illustrative, not advice.</span>{' '}
      {children ??
        'These numbers are a mirror of what you entered — nothing more. Any specific investment or tax decision needs a licensed professional.'}
    </p>
  );
}

export function Chip({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'ok' | 'near' | 'over' | 'dusk';
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-surface-sunk text-ink-soft border-border',
    ok: 'bg-sage-soft text-sage border-sage/30',
    near: 'bg-honey-soft text-honey border-honey/30',
    over: 'bg-clay-soft text-clay border-clay/30',
    dusk: 'bg-dusk-soft text-dusk border-dusk/30',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        tones[tone]
      )}
    >
      {children}
    </span>
  );
}

/** Progress bar with the value announced in text as well as drawn. */
export function ProgressBar({
  percent,
  color,
  label,
}: {
  percent: number;
  color: string;
  label: string;
}) {
  const width = Math.max(0, Math.min(100, percent));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="h-2.5 w-full overflow-hidden rounded-full bg-surface-sunk"
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${width}%`, backgroundColor: color }}
      />
    </div>
  );
}
